"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _Animated = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Animated"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _Icon = _interopRequireDefault(require("../Icon"));
var _Card = _interopRequireDefault(require("../Card"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.linkProps, _utils.viewProps]);
const selectCardStyles = styles => ['borderColor', 'borderRadius', 'borderWidth'].reduce((acc, key) => {
  if (styles[key]) acc[key] = styles[key];
  return acc;
}, {});
const selectInteractiveCardStyles = _ref => {
  let {
    backgroundColor,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop
  } = _ref;
  return {
    ...(backgroundColor && {
      backgroundColor
    }),
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop
  };
};
const selectIconStyles = _ref2 => {
  let {
    iconMarginBottom,
    iconMarginLeft,
    iconMarginRight,
    iconMarginTop
  } = _ref2;
  return {
    marginBottom: iconMarginBottom,
    marginLeft: iconMarginLeft,
    marginRight: iconMarginRight,
    marginTop: iconMarginTop
  };
};
const selectIconProps = _ref3 => {
  let {
    icon,
    iconBackgroundColor,
    iconColor
  } = _ref3;
  return {
    icon,
    variant: {
      background: true,
      padding: 'small'
    },
    tokens: {
      backgroundColor: iconBackgroundColor,
      color: iconColor
    }
  };
};
const selectTitleStyles = _ref4 => {
  let {
    titleMarginBottom,
    titleMarginLeft,
    titleMarginRight,
    titleMarginTop
  } = _ref4;
  return {
    marginBottom: titleMarginBottom,
    marginLeft: titleMarginLeft,
    marginRight: titleMarginRight,
    marginTop: titleMarginTop
  };
};
const selectTitleTextStyles = _ref5 => {
  let {
    titleFontColor,
    titleFontName,
    titleFontSize,
    titleFontWeight,
    titleLineHeight
  } = _ref5;
  return (0, _ThemeProvider.applyTextStyles)({
    fontColor: titleFontColor,
    fontName: titleFontName,
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    lineHeight: titleLineHeight
  });
};
const selectContentStyles = _ref6 => {
  let {
    contentMarginBottom,
    contentMarginLeft,
    contentMarginRight,
    contentMarginTop
  } = _ref6;
  return {
    marginBottom: contentMarginBottom,
    marginLeft: contentMarginLeft,
    marginRight: contentMarginRight,
    marginTop: contentMarginTop
  };
};
const selectContentTextStyles = _ref7 => {
  let {
    contentFontColor,
    contentFontName,
    contentFontSize,
    contentFontWeight,
    contentLineHeight
  } = _ref7;
  return (0, _ThemeProvider.applyTextStyles)({
    fontColor: contentFontColor,
    fontName: contentFontName,
    fontSize: contentFontSize,
    fontWeight: contentFontWeight,
    lineHeight: contentLineHeight
  });
};
const selectStatusIconProps = _ref8 => {
  let {
    statusIcon,
    statusIconColor
  } = _ref8;
  return {
    icon: statusIcon,
    tokens: {
      color: statusIconColor
    },
    variant: {
      size: 'micro'
    }
  };
};
const selectActionIconStyles = _ref9 => {
  let {
    actionIconMarginBottom,
    actionIconMarginLeft,
    actionIconMarginRight,
    actionIconMarginTop
  } = _ref9;
  return {
    marginBottom: actionIconMarginBottom,
    marginLeft: actionIconMarginLeft,
    marginRight: actionIconMarginRight,
    marginTop: actionIconMarginTop
  };
};
const selectActionIconProps = _ref10 => {
  let {
    actionIcon,
    actionIconColor
  } = _ref10;
  return {
    icon: actionIcon,
    tokens: {
      color: actionIconColor
    }
  };
};
const ACTION_ICON_ANIMATION_DURATION = 100;

/**
 * ActionCard component represents a card with an action that can be triggered by the user.
 *
 * @component
 * @example
 * return (
 *   <ActionCard
 *     icon={<Icon icon={Icons.EyeMasked} />}
 *     title="Like"
 *     href="/like"
 *     accessibilityRole="link"
 *     tokens={themeTokens}
 *     variant={{validation: 'warning'}}
 *     onPress={() => handleLike()}
 *   >
 *    Click here to like the post
 *   </ActionCard>
 * )
 */
const ActionCard = /*#__PURE__*/_react.default.forwardRef((_ref11, ref) => {
  let {
    icon,
    title,
    children,
    href,
    direction = true,
    accessibilityRole,
    tokens,
    variant,
    ...rest
  } = _ref11;
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('ActionCard', tokens, variant);
  const actionIconAnimation = _react.default.useRef(new _Animated.default.Value(0)).current;
  const {
    onPress,
    ...props
  } = _utils.clickProps.toPressProps(rest);
  const {
    hrefAttrs,
    rawRest
  } = _utils.hrefAttrsProp.bundle(props);
  const selectedProps = selectProps({
    accessibilityRole,
    href,
    onPress: _utils.linkProps.handleHref({
      href,
      onPress
    }),
    hrefAttrs,
    ...rawRest
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Card.default, {
    ref: ref,
    interactiveCard: {
      body: pressableState => {
        const animate = pressableState.pressed || pressableState.hover || pressableState.focus;
        _Animated.default.timing(actionIconAnimation, {
          toValue: animate ? themeTokens.actionIconTranslate : 0,
          duration: ACTION_ICON_ANIMATION_DURATION,
          useNativeDriver: false
        }).start();
        return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
          style: staticStyles.container,
          children: [icon && /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
            style: selectIconStyles(themeTokens),
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.default, {
              ...selectIconProps({
                icon,
                ...themeTokens
              })
            })
          }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
            style: staticStyles.content,
            children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
              style: staticStyles.header,
              children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
                style: [selectTitleStyles(themeTokens), staticStyles.title],
                children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Text.default, {
                  children: [(0, _utils.wrapStringsInText)(title, {
                    style: selectTitleTextStyles(themeTokens)
                  }), themeTokens.statusIcon && /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
                    style: staticStyles.statusIcon,
                    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.default, {
                      ...selectStatusIconProps(themeTokens)
                    })
                  })]
                })
              }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
                style: staticStyles.icons,
                children: direction && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Animated.default.View, {
                  style: [selectActionIconStyles(themeTokens), {
                    transform: [{
                      translateX: actionIconAnimation
                    }]
                  }],
                  children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.default, {
                    ...selectActionIconProps(themeTokens)
                  })
                })
              })]
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
              style: [staticStyles.body, selectContentStyles(themeTokens)],
              children: typeof children === 'string' ? (0, _utils.wrapStringsInText)(children, {
                style: selectContentTextStyles(themeTokens)
              }) : children
            })]
          })]
        });
      },
      tokens: selectInteractiveCardStyles(themeTokens)
    },
    tokens: selectCardStyles(themeTokens),
    ...selectedProps
  });
});
ActionCard.displayName = 'ActionCard';
ActionCard.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _utils.getTokensPropType)('ActionCard'),
  variant: _utils.variantProp.propType,
  /**
   * Icon for the ActionCard
   */
  icon: _propTypes.default.elementType,
  /**
   * Title for the ActionCard
   */
  title: _propTypes.default.string,
  /**
   * Children for the ActionCard
   */
  children: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.elementType]),
  /**
   * href for the ActionCard
   */
  href: _propTypes.default.string,
  /**
   * Enable or disable the direction of the ActionCard
   */
  direction: _propTypes.default.bool,
  /**
   * AccesibilityRole for the ActionCard
   */
  accessibilityRole: _propTypes.default.string
};
const staticStyles = _StyleSheet.default.create({
  body: {
    flexDirection: 'column'
  },
  container: {
    flex: 1,
    flexDirection: 'row'
  },
  content: {
    flex: 1,
    flexDirection: 'column'
  },
  icons: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  title: {
    flexShrink: 1
  },
  statusIcon: {
    marginLeft: 4
  }
});
var _default = exports.default = ActionCard;